'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import {
  TelegramSellerPublicProfileResponseDto,
  TelegramProductShortResponseDto,
  useTelegramProductsFindSellerPublicProducts,
} from '@/generated';
import { ProductBigPreview } from '../../components/shared/Product/productBigPreview';
import { IconBrandWaze, IconExternalLink, IconMapPin } from '@tabler/icons-react';
import { SocialIcon } from '../../components/shared/Icons/SocialIcon';
import { buildSocialLinkItems, type SocialLinkItem } from '@/utils/buildSocialLinkItems';
import { isMaklerSellerPublicProfile } from '@/utils/userFlags';
import { ExpandableText } from '../../components/shared/ExpandableText';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ShareButton } from '../../components/shared/Product/ShareButton';
import { getUserAvatarColor } from '@/utils/getAvatarBg';
import { formatMemberSince, getLastVisitInfo } from '@/utils/formatDayOrTime';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import { Spinner } from '@/components/ui/spinner';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { SeoTextBlock } from '../../components/shared/SeoTextBlock';

const PER_PAGE = 20;

type Props = {
  profile: TelegramSellerPublicProfileResponseDto;
  sellerPublicProfileId: string;
  seoHeader: string;
  seoBlockHeader: string;
  seoBlockText: string;
};

function IconLinkVisits() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71' />
      <path d='M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71' />
    </svg>
  );
}

function StatRow({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className='flex items-center gap-[10px] py-[10px]'>
      <span className='shrink-0 text-text-disabled-day'>{icon}</span>
      <span className='flex-1 text-[14px] font-normal text-text-primary leading-[20px]'>{label}</span>
      <span className='text-[14px] font-normal text-text-primary leading-[20px] tabular-nums'>
        {value.toLocaleString()}
      </span>
    </div>
  );
}

function IconListings() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='3' y='3' width='7' height='7' rx='1' />
      <rect x='14' y='3' width='7' height='7' rx='1' />
      <rect x='3' y='14' width='7' height='7' rx='1' />
      <rect x='14' y='14' width='7' height='7' rx='1' />
    </svg>
  );
}

function IconViews() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
      <circle cx='12' cy='12' r='3' />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.4 11.22 19.79 19.79 0 01.34 2.6 2 2 0 012.33.5h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.09a16 16 0 006 6l.76-.76a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z' />
    </svg>
  );
}

function IconChat() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M8.625 17.625H15.375L18.75 21V17.625H21V8.625H18.75M3 3H15.375V12H8.625L5.25 15.375V12H3V3Z' />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z' />
    </svg>
  );
}

function SocialLinkRow({ item }: { item: SocialLinkItem }) {
  return (
    <a
      href={item.href}
      target='_blank'
      rel='noopener noreferrer'
      className='group flex w-fit items-center gap-[10px] min-w-0'
    >
      <SocialIcon label={item.label} className='size-5 lg:size-6' />
      <span className='flex items-center gap-[2px]'>
        <span className='text-[14px] font-medium text-primary-green-day leading-[20px] transition-opacity group-hover:opacity-70'>
          {item.label}
        </span>
        <IconExternalLink
          size={14}
          className='text-primary-green-day shrink-0 rtl:-scale-x-100 transition-transform group-hover:translate-x-[2px] group-hover:-translate-y-[2px] rtl:group-hover:-translate-x-[2px]'
        />
      </span>
    </a>
  );
}

function InfoLink({ href, icon, children }: { href: string; icon: ReactNode; children: ReactNode }) {
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className='group flex w-fit items-center gap-[8px] text-primary-green-day'
    >
      <span className='shrink-0'>{icon}</span>
      <span className='flex items-center gap-[2px]'>
        <span className='transition-opacity group-hover:opacity-70'>{children}</span>
        <IconExternalLink
          size={14}
          className='shrink-0 rtl:-scale-x-100 transition-transform group-hover:translate-x-[2px] group-hover:-translate-y-[2px] rtl:group-hover:-translate-x-[2px]'
        />
      </span>
    </a>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex flex-col gap-[4px] py-[10px]'>
      <span className='text-[13px] text-text-disabled-day leading-[16px]'>{label}</span>
      <div className='text-[14px] font-medium text-text-primary leading-[20px] wrap-break-word'>{children}</div>
    </div>
  );
}

function ProductGridSkeleton() {
  return (
    <div className='grid grid-cols-2 lg:grid-cols-3 gap-[12px] lg:gap-[16px]'>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className='flex flex-col gap-[8px]'>
          <Skeleton className='aspect-square rounded-[12px]' />
          <Skeleton className='h-[16px] rounded-sm w-3/4' />
          <Skeleton className='h-[16px] rounded-sm w-1/2' />
        </div>
      ))}
    </div>
  );
}

export function SellerProfileClient({
  profile,
  sellerPublicProfileId,
  seoHeader,
  seoBlockHeader,
  seoBlockText,
}: Props) {
  const t = useTranslations('sellerProfile');
  const locale = useLocale();
  const isDesktop = useMediaQuerySafe('(min-width: 1024px)');
  const [openSections, setOpenSections] = useState<string[]>([]);

  useEffect(() => {
    setOpenSections((prev) => {
      const rest = prev.filter((x) => x !== 'sellerInfo');
      return isDesktop ? [...rest, 'sellerInfo'] : rest;
    });
  }, [isDesktop]);

  const [skip, setSkip] = useState(0);
  const [allProducts, setAllProducts] = useState<TelegramProductShortResponseDto[]>([]);
  const prevSkipRef = useRef(-1);

  const { data, isFetching } = useTelegramProductsFindSellerPublicProducts(sellerPublicProfileId, {
    status: 'Active',
    take: PER_PAGE,
    skip,
  });

  useEffect(() => {
    if (!data || prevSkipRef.current === skip) return;
    prevSkipRef.current = skip;
    if (skip === 0) {
      setAllProducts(data.data);
    } else {
      setAllProducts((prev) => [...prev, ...data.data]);
    }
  }, [data, skip]);

  const total = data?.total ?? 0;
  const hasMore = allProducts.length < total;

  const handleLoadMore = useCallback(() => {
    setSkip((prev) => prev + PER_PAGE);
  }, []);

  const avatarColor = getUserAvatarColor(profile.name);
  const memberSince = formatMemberSince(profile.createdAt, locale);
  const lastVisitInfo = getLastVisitInfo(profile.lastOnlineAt);
  const { statistics, socialLinks } = profile;

  const isOnline = profile.isOnline;

  const isMakler = isMaklerSellerPublicProfile(profile);
  const socialLinkItems = buildSocialLinkItems(socialLinks);
  const hasAboutMe = !!profile.aboutMe;
  const userTypeText = isMakler
    ? t('userTypeMakler')
    : profile.isBusiness
      ? t('userTypeBusiness')
      : t('userTypePrivate');
  const companyName = profile.isBusiness ? profile.companyName : null;
  const companyAddress = profile.isBusiness ? profile.google?.address : null;
  const companyMapsUrl = profile.isBusiness ? profile.google?.mapsUrl : null;
  const companyWazeUrl = profile.isBusiness ? profile.wazeUrl : null;
  const companyGoogleProfileUrl = profile.isBusiness ? profile.google?.profileUrl : null;
  const hasCompanyAddress = !!companyAddress || !!companyMapsUrl || !!companyWazeUrl;
  return (
    <div className='flex flex-col lg:flex-row gap-[24px] lg:gap-[32px] lg:items-start mt-[24px]'>
      <aside className='lg:sticky lg:top-[90px] lg:self-start lg:w-[300px] w-full'>
        <div
          className='bg-white rounded-[20px] px-[20px] py-[24px] flex flex-col'
          style={{ boxShadow: '0px 3px 12px 0px #28419B1F' }}
        >
          <div className='flex flex-col items-start gap-[12px]'>
            <div className='relative size-[80px]'>
              <div
                className={
                  'flex items-center justify-center overflow-hidden text-[32px] font-semibold text-white size-[80px] rounded-full ' +
                  avatarColor
                }
              >
                {profile.photoUrl ? (
                  <Image
                    src={profile.photoUrl}
                    alt={profile.name}
                    className='size-full object-cover'
                    width={80}
                    height={80}
                  />
                ) : (
                  <span>{profile.name.at(0)?.toUpperCase()}</span>
                )}
              </div>
              {isOnline && (
                <span className='absolute bottom-[6px] end-[6px] size-[12px] rounded-full bg-primary-green-day border-2 border-white' />
              )}
            </div>

            <div className='flex items-start gap-[8px] w-full justify-between'>
              <h1 className='text-[16px] font-medium text-text-primary leading-[22px] wrap-break-word min-w-0'>
                {seoHeader || profile.name}
              </h1>
              <div className='shrink-0'>
                <ShareButton
                  title={profile.name}
                  url={`/u/${sellerPublicProfileId}`}
                  size={24}
                  copiedText={t('url_copied')}
                />
              </div>
            </div>

            <div className='w-full flex flex-col gap-0 mt-[-4px]'>
              <div className='flex items-center justify-between py-[6px]'>
                {isOnline ? (
                  <>
                    <span className='text-[14px] font-normal text-text-primary'>{t('statusLabel')}</span>
                    <span className='text-[14px] font-medium text-primary-green-day'>{t('onlineNow')}</span>
                  </>
                ) : (
                  <>
                    <span className='text-[14px] font-normal text-text-primary'>{t('lastOnline')}</span>
                    <span className='text-[14px] font-medium text-text-primary'>{t(lastVisitInfo)}</span>
                  </>
                )}
              </div>
              <div className='py-[6px] text-[14px] text-text-disabled-day'>
                {t('memberSinceLabel')} {t('memberSinceDate', { date: memberSince })}
              </div>
            </div>
          </div>

          <Accordion type='multiple' value={openSections} onValueChange={setOpenSections}>
            <AccordionItem value='sellerInfo' className='border-b-0 mt-[16px]'>
              <AccordionTrigger className='items-center py-[2px] text-[16px] leading-[22px] font-medium text-text-primary rounded-none hover:no-underline [&_svg]:size-[18px] [&_svg]:text-text-disabled-day'>
                {t('sellerInfoTitle')}
              </AccordionTrigger>
              <AccordionContent className='pb-0'>
                <div className='flex flex-col divide-y divide-solid divide-primary-stroke-day mt-[4px]'>
                  {companyName && (
                    <div className='flex flex-col gap-[4px] py-[10px]'>
                      <span className='text-[14px] text-text-disabled-day leading-[20px]'>{t('companyNameLabel')}</span>
                      <span className='text-[14px] text-text-primary leading-[20px] wrap-break-word'>
                        {companyName}
                      </span>
                    </div>
                  )}

                  <div className='flex flex-wrap items-baseline gap-x-[8px] py-[10px] text-[14px] leading-[20px]'>
                    <span className='text-text-disabled-day'>{t('userTypeLabel')}</span>
                    <span className='text-text-primary'>{userTypeText}</span>
                  </div>

                  {isMakler && profile.maklerLicenseUrl && (
                    <div className='flex flex-wrap items-baseline gap-x-[8px] py-[10px] text-[14px] leading-[20px]'>
                      <span className='text-text-disabled-day'>{t('maklerLicenseLabel')}</span>
                      <a
                        href={profile.maklerLicenseUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-text-primary underline underline-offset-4 transition-opacity hover:opacity-70'
                      >
                        {t('maklerLicenseCheckLink')}
                      </a>
                    </div>
                  )}
                  {hasAboutMe && (
                    <InfoRow label={t('aboutCompanyLabel')}>
                      <ExpandableText
                        text={profile.aboutMe as string}
                        className='font-normal text-text-primary'
                        expandLabel={t('showFullText')}
                        collapseLabel={t('collapseText')}
                      />
                    </InfoRow>
                  )}
                  {hasCompanyAddress && (
                    <InfoRow label={t('companyAddressLabel')}>
                      <div className='flex flex-col gap-[6px]'>
                        {companyAddress && <span className='font-normal'>{companyAddress}</span>}
                        {companyMapsUrl && (
                          <InfoLink href={companyMapsUrl} icon={<IconMapPin size={18} stroke={1.75} />}>
                            {t('openOnMap')}
                          </InfoLink>
                        )}
                        {companyWazeUrl && (
                          <InfoLink href={companyWazeUrl} icon={<IconBrandWaze size={18} stroke={1.75} />}>
                            {t('openInWaze')}
                          </InfoLink>
                        )}
                      </div>
                    </InfoRow>
                  )}
                  {companyGoogleProfileUrl && (
                    <InfoRow label={t('googleProfileLabel')}>
                      <InfoLink
                        href={companyGoogleProfileUrl}
                        icon={<Image src='/assets/images/devicon_google.png' width={18} height={18} alt='' />}
                      >
                        {t('openGoogleProfile')}
                      </InfoLink>
                    </InfoRow>
                  )}
                  {socialLinkItems.length > 0 && (
                    <InfoRow label={t('socialLinksLabel')}>
                      <div className='flex flex-col gap-[8px] font-normal'>
                        {socialLinkItems.map((item) => (
                          <SocialLinkRow key={item.label} item={item} />
                        ))}
                      </div>
                    </InfoRow>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value='stats' className='border-b-0 mt-[8px]'>
              <AccordionTrigger className='items-center py-[2px] text-[16px] leading-[22px] font-medium text-text-primary rounded-none hover:no-underline [&_svg]:size-[18px] [&_svg]:text-text-disabled-day'>
                {t('statsTitle')}
              </AccordionTrigger>
              <AccordionContent className='pb-0'>
                <div className='flex flex-col mt-[4px]'>
                  <StatRow icon={<IconListings />} label={t('activeProducts')} value={statistics.totalActiveProducts} />
                  <StatRow icon={<IconViews />} label={t('totalViews')} value={statistics.totalViews} />
                  <StatRow icon={<IconPhone />} label={t('phoneRequests')} value={statistics.phoneRequests} />
                  <StatRow icon={<IconChat />} label={t('chatRequests')} value={statistics.chatRequests} />
                  <StatRow icon={<IconHeart />} label={t('favourites')} value={statistics.favourites} />
                  <StatRow icon={<IconLinkVisits />} label={t('linkVisits')} value={statistics.linkVisits ?? 0} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </aside>

      <main className='flex-1 min-w-0'>
        <div className='flex flex-col gap-[16px]'>
          <div className='flex flex-col gap-[12px]'>
            <h2 className='text-[20px] font-semibold text-text-primary leading-[24px]'>{t('title')}</h2>
          </div>

          {prevSkipRef.current === -1 && isFetching ? (
            <ProductGridSkeleton />
          ) : allProducts.length === 0 ? (
            <Empty className='gap-[16px] px-0 py-[60px] md:px-0 md:py-[60px]'>
              <EmptyMedia className='mb-0'>
                <Image src='/assets/images/not-found-ad.svg' width={120} height={120} alt='' />
              </EmptyMedia>
              <EmptyHeader className='gap-[6px]'>
                <EmptyTitle className='text-[16px] font-medium tracking-normal text-text-primary'>
                  {t('noProducts')}
                </EmptyTitle>
                <EmptyDescription className='text-[14px] font-normal text-text-disabled-day'>
                  {t('noProductsHint')}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              <div className='grid grid-cols-2 lg:grid-cols-3 gap-[12px] lg:gap-[16px]'>
                {allProducts.map((product) => (
                  <ProductBigPreview key={product.id} product={product} forceSquare />
                ))}
              </div>

              {hasMore && (
                <div className='flex justify-center mt-[8px]'>
                  <Button
                    variant='outline'
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    className='h-auto px-[32px] py-[12px] border-border-light text-[14px] text-text-primary hover:bg-bg2-day'
                  >
                    {isFetching ? (
                      <>
                        <Spinner className='size-[16px] text-text-disabled-day' />
                        <span>{t('loadMore')}</span>
                      </>
                    ) : (
                      t('loadMore')
                    )}
                  </Button>
                </div>
              )}

              {isFetching && prevSkipRef.current !== -1 && (
                <div className='grid grid-cols-2 lg:grid-cols-3 gap-[12px] lg:gap-[16px] mt-[4px]'>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className='flex flex-col gap-[8px]'>
                      <Skeleton className='aspect-square rounded-[12px]' />
                      <Skeleton className='h-[16px] rounded-sm w-3/4' />
                      <Skeleton className='h-[16px] rounded-sm w-1/2' />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <SeoTextBlock header={seoBlockHeader} seoText={seoBlockText} />
      </main>
    </div>
  );
}
